import { v4 } from 'uuid'
import { customRequestListeners, type RequestStartedEvent } from '#api/apiListeners'
import { finishApiRequest, startApiRequest } from '#store/requestObserverSlice'
import type { SharedStore } from '#store/sharedBaseStore'

const maxResponseAge = 30 * 1000
const maxTimeDiff = 100
const apiPrefix = `${location.origin}/api/`

type RequestStartedWithId = { id: string; startedAtUtcTimestamp: Timestamp; metadata?: Record<string, unknown> } & RequestStartedEvent
type MetadataCollector = (evt: RequestStartedEvent) => Record<string, unknown> | undefined

class ApiRequestPerformanceObserver {
  // sorted by receivedAt ascending
  private pendingResponses: RequestStartedWithId[] = []
  private readonly apiMetadataCollectors: Set<MetadataCollector> = new Set()
  private store: SharedStore | null = null
  private readonly observer: PerformanceObserver
  private isObserving = false

  constructor() {
    this.observer = new PerformanceObserver(this.performanceObserverListener)
  }

  public registerApiMetadataCollector(collector: MetadataCollector) {
    this.apiMetadataCollectors.add(collector)
  }

  public unregisterApiMetadataCollector(collector: MetadataCollector) {
    this.apiMetadataCollectors.delete(collector)
  }

  public connectStore(store: SharedStore | null) {
    this.store = store
    if (this.store) {
      this.setEnabled(this.store.getState().requestObserver.isEnabled)
    }
  }

  public setEnabled(enabled: boolean) {
    if (!this.isObserving && enabled) {
      this.observer.observe({ type: 'resource', buffered: true })
      customRequestListeners.add(this.requestStartedListener)
    } else if (this.isObserving && !enabled) {
      this.observer.disconnect()
      customRequestListeners.delete(this.requestStartedListener)
    }
  }

  private collectMetadataForRequest(evt: RequestStartedEvent): Record<string, unknown> {
    const metadata: Record<string, unknown> = {}
    for (const collector of this.apiMetadataCollectors) {
      try {
        const collected = collector(evt)
        Object.assign(metadata, collected)
      } catch (e) {
        console.error('API metadata collector failed', e)
      }
    }
    return metadata
  }

  private requestStartedListener = (evt: RequestStartedEvent) => {
    // console.log('[Performance] Request started for', evt)
    const now = performance.now()
    const metadata = this.collectMetadataForRequest(evt)

    const evtWithId: RequestStartedWithId = {
      ...evt,
      startedAtUtcTimestamp: Date.now(),
      id: v4(),
      metadata,
    }
    this.pendingResponses = this.pendingResponses.filter((e) => e.startedAt > now - maxResponseAge)

    this.pendingResponses.push(evtWithId)
    this.pendingResponses.sort((a, b) => a.startedAt - b.startedAt)

    this.store?.dispatch(
      startApiRequest({
        type: 'pending',
        startedAtUtcTimestamp: evtWithId.startedAtUtcTimestamp,
        id: evtWithId.id,
        url: evtWithId.url,
        body: evtWithId.body,
        method: evtWithId.method,
        startTime: evtWithId.startedAt,
        endpoint: evtWithId.endpoint,
        metadata,
      }),
    )
  }

  private findClosestResponseForUrlAndTime(url: string, responseTime: DOMHighResTimeStamp): RequestStartedWithId | undefined {
    for (const evt of this.pendingResponses) {
      if (evt.startedAt >= responseTime + maxTimeDiff) {
        return undefined
      }
      if (evt.url === url) {
        return evt
      }
    }
    return undefined
  }

  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: is used for the custom request listener
  private performanceObserverListener = (list: PerformanceObserverEntryList, _observer: PerformanceObserver) => {
    const entries = list.getEntries().filter((entry): entry is PerformanceResourceTiming => entry.name.startsWith(apiPrefix))
    if (entries.length === 0) {
      return
    }

    for (const entry of entries) {
      const matchingResponse = this.findClosestResponseForUrlAndTime(entry.name, entry.requestStart)

      if (matchingResponse) {
        // console.log('[Performance] Matched', entry, 'to', matchingResponse)
        this.pendingResponses = this.pendingResponses.filter((e) => e.id !== matchingResponse.id)

        this.store?.dispatch(
          finishApiRequest({
            type: 'finished',
            id: matchingResponse.id,
            startedAtUtcTimestamp: matchingResponse.startedAtUtcTimestamp,
            url: entry.name,
            method: matchingResponse.method,
            body: matchingResponse.body,
            endpoint: matchingResponse.endpoint,
            metadata: matchingResponse.metadata,
            connectEnd: entry.connectEnd,
            connectStart: entry.connectStart,
            contentType: entry.nextHopProtocol,
            decodedBodySize: entry.decodedBodySize,
            domainLookupEnd: entry.domainLookupEnd,
            domainLookupStart: entry.domainLookupStart,
            duration: entry.duration,
            encodedBodySize: entry.encodedBodySize,
            fetchStart: entry.fetchStart,
            redirectEnd: entry.redirectEnd,
            redirectStart: entry.redirectStart,
            requestStart: entry.requestStart,
            responseEnd: entry.responseEnd,
            responseStart: entry.responseStart,
            responseStatus: entry.responseStatus ?? -1,
            secureConnectionStart: entry.secureConnectionStart,
            startTime: entry.startTime,
            transferSize: entry.transferSize,
            serverTiming: entry.serverTiming.map((st) => ({
              description: st.description,
              duration: st.duration,
              name: st.name,
            })),
          }),
        )
      } else {
        console.warn('[Performance] No matching response for', entry)
      }
    }
  }
}

export const apiRequestPerformanceObserver = new ApiRequestPerformanceObserver()

// if (initialState.isEnabled) {
//   observer.observe({ type: 'resource', buffered: true })
//   customRequestListeners.add(requestStartedListener)
// }
